import React, { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import {
  isPushSupported,
  isNativeApp,
  getSubscriptionState,
  registerAndSubscribe,
  unsubscribe,
  openAppNotificationSettings,
} from '../lib/pushNotifications';

/**
 * Bell button that lets users opt-in / opt-out of daily push notifications
 * (Article du jour, tous les matins à 07:00 heure de Kinshasa).
 */
const PushToggle = () => {
  const [state, setState] = useState('default');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }
    getSubscriptionState().then(setState);
  }, []);

  if (state === 'unsupported') return null;

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 3200);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await registerAndSubscribe();
      setState('subscribed');
      flash('Notifications activées — vous recevrez l\'article du jour à 7h.');
    } catch (err) {
      if (err.message === 'permission-denied') {
        setState('blocked');
        flash(isNativeApp()
          ? 'Notifications refusees. Ouvrez les reglages pour les autoriser.'
          : 'Notifications refusees. Autorisez-les dans les reglages du navigateur.');
      } else {
        flash('Impossible d\'activer les notifications.');
      }
    } finally {
      setBusy(false);
    }
  };

  const openSettings = async () => {
    const ok = await openAppNotificationSettings();
    if (!ok) flash('Ouvrez les reglages de votre telephone pour autoriser Mibeko.');
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribe();
      setState('not-subscribed');
      flash('Notifications désactivées.');
    } catch {
      flash('Erreur lors de la désactivation.');
    } finally {
      setBusy(false);
    }
  };

  const enabled = state === 'subscribed';
  const nativeBlocked = state === 'blocked' && isNativeApp();
  const disabled = busy || (state === 'blocked' && !isNativeApp());

  const handleClick = () => {
    if (nativeBlocked) return openSettings();
    return enabled ? disable() : enable();
  };

  return (
    <div className="push-toggle" data-testid="push-toggle">
      <button
        className={`push-toggle-btn ${enabled ? 'is-on' : ''}`}
        onClick={handleClick}
        disabled={disabled}
        aria-label={enabled ? 'Désactiver les notifications' : 'Activer les notifications quotidiennes'}
        title={
          nativeBlocked
            ? 'Notifications bloquées — ouvrir les réglages de l\'app'
            : state === 'blocked'
            ? 'Notifications bloquées — modifiez les réglages du navigateur'
            : enabled
              ? 'Notifications quotidiennes activées'
              : 'Recevoir l\'article du jour chaque matin à 7h'
        }
        data-testid={enabled ? 'push-toggle-off' : 'push-toggle-on'}
      >
        {enabled ? (
          <Bell style={{ width: 18, height: 18 }} />
        ) : (
          <BellOff style={{ width: 18, height: 18 }} />
        )}
      </button>
      {msg && (
        <span className="push-toggle-msg" role="status" data-testid="push-toggle-msg">
          {msg}
        </span>
      )}
    </div>
  );
};

export default PushToggle;
